import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import Swal from 'sweetalert2';
import { swalSuccess, swalError, swalConfirm } from '../utils/alert';
import './CardList.css';
import './RevenueDesktop.css';
import './MobileApp.css';

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/D';
  const date = new Date(dateString);
  // add a day to fix timezone issue
  date.setDate(date.getDate() + 1);
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const getStatusClass = (status) => {
  if (status === 'Recebida') return 'recebida';
  if (status === 'Pendente') return 'pendente';
  if (status === 'Atrasada') return 'atrasada';
  return '';
};

function RevenueList({ onEdit, refreshTrigger, onNewRevenue }) {
  const [revenues, setRevenues] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    description: '',
    due_date_start: '',
    due_date_end: '',
    status: '',
    amount_min: '',
    amount_max: ''
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const width = useWindowWidth();


  const fetchRevenues = (url = '/revenues', currentFilters = filters) => {
    const urlObj = new URL(url, window.location.origin);
    const page = urlObj.searchParams.get('page');
    const params = new URLSearchParams(currentFilters);
    if (page) {
      params.set('page', page);
    }
    const pathname = urlObj.pathname.replace(/^\/api/, '');
    const fullUrl = `${pathname}?${params.toString()}`;

    api.get(fullUrl)
      .then(response => {
        setRevenues(response.data.data);
        setPagination(response.data);
      })
      .catch(error => {
        console.error('Houve um erro ao buscar as receitas!', error);
      });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = () => {
    fetchRevenues('/revenues');
    if (width < 768) {
      setShowFilterModal(false);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      description: '',
      due_date_start: '',
      due_date_end: '',
      status: '',
      amount_min: '',
      amount_max: ''
    };
    setFilters(clearedFilters);
    fetchRevenues('/revenues', clearedFilters);
    if (width < 768) {
      setShowFilterModal(false);
    }
  };

  useEffect(() => {
    fetchRevenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleClearSimulations = async () => {
    const result = await swalConfirm({
      title: 'Limpar Simulações',
      text: 'Todas as receitas marcadas como simulação serão excluídas permanentemente. Esta ação não pode ser desfeita.',
      confirmText: 'Sim, limpar tudo',
    });
    if (!result.isConfirmed) return;
    try {
      const { data } = await api.delete('/revenues/simulations');
      swalSuccess(`${data.deleted} simulação(ões) excluída(s) com sucesso!`);
      fetchRevenues(pagination?.current_page_url || '/revenues');
    } catch (error) {
      swalError(error?.response?.data?.message || 'Erro ao limpar simulações.');
    }
  };

  const handlePromoteToReal = async (revenue) => {
    const result = await swalConfirm({
      title: 'Tornar Lançamento Real',
      text: 'Esta receita deixará de ser uma simulação e passará a ser um lançamento real. Deseja continuar?',
      confirmText: 'Sim, tornar real',
    });
    if (!result.isConfirmed) return;
    try {
      await api.patch(`/revenues/${revenue.id}/promote`);
      swalSuccess('Receita convertida para lançamento real com sucesso!');
      fetchRevenues(pagination?.current_page_url || '/revenues');
    } catch (error) {
      swalError(error?.response?.data?.message || 'Erro ao converter receita.');
    }
  };

  const handleDelete = async (revenue) => {
    const { id, status, installment_number: num, total_installments: total, revenue_group_id: groupId } = revenue;

    if (status === 'Recebida') {
      swalError('Esta parcela já possui baixa e não pode ser excluída.');
      return;
    }

    const hasSubsequent = groupId && num && total && num < total;

    if (hasSubsequent) {
      const { isConfirmed, isDenied } = await Swal.fire({
        title: `Excluir parcela ${num}/${total}`,
        text: 'Deseja excluir apenas esta parcela ou também as subsequentes?',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: `Esta e as subsequentes (${num} a ${total})`,
        denyButtonText: 'Apenas esta parcela',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        denyButtonColor: '#6c757d',
        cancelButtonColor: '#aaa',
      });

      if (isConfirmed) {
        const confirm2 = await swalConfirm({
          title: 'Confirmar exclusão em lote',
          text: `Serão excluídas as parcelas ${num} a ${total} que não possuem baixa. Esta ação não pode ser desfeita.`,
          confirmText: 'Sim, excluir',
        });
        if (!confirm2.isConfirmed) return;
        try {
          const { data } = await api.delete(`/revenues/${id}/subsequent`);
          const msg = data.skipped > 0
            ? `${data.deleted} parcela(s) excluída(s). ${data.skipped} parcela(s) com baixa foram mantidas.`
            : `${data.deleted} parcela(s) excluída(s) com sucesso!`;
          swalSuccess(msg);
          fetchRevenues(pagination?.current_page_url || '/revenues');
        } catch (error) {
          swalError(error?.response?.data?.message || 'Erro ao excluir parcelas.');
        }
      } else if (isDenied) {
        const confirm2 = await swalConfirm({ text: 'Tem certeza que deseja excluir apenas esta parcela?' });
        if (!confirm2.isConfirmed) return;
        try {
          await api.delete(`/revenues/${id}`);
          swalSuccess('Parcela excluída com sucesso!');
          fetchRevenues(pagination?.current_page_url || '/revenues');
        } catch (error) {
          swalError(error?.response?.data?.message || 'Erro ao excluir parcela.');
        }
      }
    } else {
      const result = await swalConfirm({ text: 'Tem certeza que deseja excluir esta receita?' });
      if (!result.isConfirmed) return;
      try {
        await api.delete(`/revenues/${id}`);
        swalSuccess('Receita excluída com sucesso!');
        fetchRevenues(pagination?.current_page_url || '/revenues');
      } catch (error) {
        swalError(error?.response?.data?.message || 'Erro ao excluir receita.');
      }
    }
  };

  const decodePaginationLabel = (label) => {
    return label
      .replace(/&laquo;/g, '«')
      .replace(/&raquo;/g, '»')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  // ── Paginação original (mobile) ──
  const renderPaginationLinks = () => {
    if (!pagination || !pagination.links) {
      return null;
    }

    return (
      <nav>
        <ul className="pagination">
          {pagination.links.map((link, index) => (
            <li key={index} className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => link.url && fetchRevenues(link.url)}
              >
                {decodePaginationLabel(link.label)}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  };

  // ── Paginação desktop (moderna) ──
  const renderDesktopPagination = () => {
    if (!pagination || !pagination.links) return null;
    return (
      <div className="rv-pagination-wrap">
        <ul className="pagination">
          {pagination.links.map((link, index) => (
            <li key={index} className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => link.url && fetchRevenues(link.url)}>
                {decodePaginationLabel(link.label)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // ── Filtros originais (usados no modal mobile) ──
  const renderFilters = () => (
    <div className="card card-body mb-3">
      <div className="row">
        <div className="col-md-4">
          <label>Descrição</label>
          <input type="text" name="description" value={filters.description} onChange={handleFilterChange} className="form-control" />
        </div>
        <div className="col-md-4">
          <label>Vencimento De</label>
          <input type="date" name="due_date_start" value={filters.due_date_start} onChange={handleFilterChange} className="form-control" />
        </div>
        <div className="col-md-4">
          <label>Vencimento Até</label>
          <input type="date" name="due_date_end" value={filters.due_date_end} onChange={handleFilterChange} className="form-control" />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-md-4">
          <label>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} className="form-control">
            <option value="">Todos</option>
            <option value="Pendente">Pendente</option>
            <option value="Recebida">Recebida</option>
          </select>
        </div>
        <div className="col-md-4">
          <label>Valor Mínimo</label>
          <input type="number" name="amount_min" value={filters.amount_min} onChange={handleFilterChange} className="form-control" />
        </div>
        <div className="col-md-4">
          <label>Valor Máximo</label>
          <input type="number" name="amount_max" value={filters.amount_max} onChange={handleFilterChange} className="form-control" />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-md-12">
          <button className="btn btn-primary me-2" onClick={handleApplyFilters}>Aplicar Filtros</button>
          <button className="btn btn-secondary" onClick={handleClearFilters}>Limpar Filtros</button>
        </div>
      </div>
    </div>
  );

  // ── Filtros desktop (modernos) ──
  const renderDesktopFilters = () => (
    <div className="rv-filters-panel">
      <div className="rv-filter-group">
        <label>Descrição</label>
        <input type="text" name="description" value={filters.description} onChange={handleFilterChange} className="rv-filter-input" placeholder="Buscar..." />
      </div>
      <div className="rv-filter-group">
        <label>Vencimento De</label>
        <input type="date" name="due_date_start" value={filters.due_date_start} onChange={handleFilterChange} className="rv-filter-input" />
      </div>
      <div className="rv-filter-group">
        <label>Vencimento Até</label>
        <input type="date" name="due_date_end" value={filters.due_date_end} onChange={handleFilterChange} className="rv-filter-input" />
      </div>
      <div className="rv-filter-group">
        <label>Status</label>
        <select name="status" value={filters.status} onChange={handleFilterChange} className="rv-filter-select">
          <option value="">Todos</option>
          <option value="Pendente">Pendente</option>
          <option value="Recebida">Recebida</option>
        </select>
      </div>
      <div className="rv-filter-group">
        <label>Valor Mínimo</label>
        <input type="number" name="amount_min" value={filters.amount_min} onChange={handleFilterChange} className="rv-filter-input" placeholder="0,00" />
      </div>
      <div className="rv-filter-group">
        <label>Valor Máximo</label>
        <input type="number" name="amount_max" value={filters.amount_max} onChange={handleFilterChange} className="rv-filter-input" placeholder="0,00" />
      </div>
      <div className="rv-filter-actions">
        <button className="rv-btn rv-btn-primary" onClick={handleApplyFilters}>Aplicar</button>
        <button className="rv-btn rv-btn-outline" onClick={handleClearFilters}>Limpar</button>
      </div>
    </div>
  );

  // ════════════════════════════════════
  // MOBILE HELPERS
  // ════════════════════════════════════
  const renderMobileFilters = () => (
    <div style={{ padding: '0 1.2rem 1rem' }}>
      <div className="mapp-filter-field">
        <label className="mapp-filter-label">Descrição</label>
        <input
          type="text"
          name="description"
          value={filters.description}
          onChange={handleFilterChange}
          className="mapp-filter-input"
          placeholder="Buscar..."
          autoComplete="off"
        />
      </div>
      <div className="mapp-filter-row">
        <div className="mapp-filter-field">
          <label className="mapp-filter-label">Vencimento De</label>
          <input
            type="date"
            name="due_date_start"
            value={filters.due_date_start}
            onChange={handleFilterChange}
            className="mapp-filter-input"
            autoComplete="off"
          />
        </div>
        <div className="mapp-filter-field">
          <label className="mapp-filter-label">Vencimento Até</label>
          <input
            type="date"
            name="due_date_end"
            value={filters.due_date_end}
            onChange={handleFilterChange}
            className="mapp-filter-input"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="mapp-filter-field">
        <label className="mapp-filter-label">Status</label>
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          className="mapp-filter-select"
        >
          <option value="">Todos</option>
          <option value="Pendente">Pendente</option>
          <option value="Recebida">Recebida</option>
        </select>
      </div>
      <div className="mapp-filter-row">
        <div className="mapp-filter-field">
          <label className="mapp-filter-label">Valor Mínimo</label>
          <input
            type="number"
            name="amount_min"
            value={filters.amount_min}
            onChange={handleFilterChange}
            className="mapp-filter-input"
            placeholder="0,00"
            autoComplete="off"
          />
        </div>
        <div className="mapp-filter-field">
          <label className="mapp-filter-label">Valor Máximo</label>
          <input
            type="number"
            name="amount_max"
            value={filters.amount_max}
            onChange={handleFilterChange}
            className="mapp-filter-input"
            placeholder="0,00"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="mapp-filter-actions">
        <button className="mapp-btn-apply" onClick={handleApplyFilters}>Aplicar</button>
        <button className="mapp-btn-clear" onClick={handleClearFilters}>Limpar</button>
      </div>
    </div>
  );

  const renderMobilePagination = () => {
    if (!pagination || !pagination.links) return null;
    return (
      <div className="mapp-pagination">
        {pagination.links.map((link, index) => (
          <button
            key={index}
            className={`mapp-page-btn ${link.active ? 'active' : ''}`}
            onClick={() => link.url && fetchRevenues(link.url)}
            disabled={!link.url}
          >
            {decodePaginationLabel(link.label)}
          </button>
        ))}
      </div>
    );
  };

  // ════════════════════════════════════
  // MOBILE VIEW — experiência nativa
  // ════════════════════════════════════
  const renderMobileView = () => {
    const hasActiveFilters = Object.values(filters).some(v => v !== '');
    return (
      <div>
        {/* Topbar */}
        <div className="mapp-topbar">
          <span className="mapp-topbar-title">Receitas</span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="mapp-sim-clear-btn" onClick={handleClearSimulations} title="Limpar simulações">
              🧪 Limpar Sim.
            </button>
            <button
              className={`mapp-filter-btn ${hasActiveFilters ? 'active' : ''}`}
              onClick={() => setShowFilterModal(true)}
            >
              ⊞ Filtros{hasActiveFilters ? ' ●' : ''}
            </button>
          </div>
        </div>

        {/* Filter bottom sheet */}
        {showFilterModal && (
          <>
            <div className="mapp-sheet-overlay" onClick={() => setShowFilterModal(false)} />
            <div className="mapp-sheet">
              <div className="mapp-sheet-handle" />
              <div className="mapp-sheet-header">
                <span className="mapp-sheet-title">Filtrar Receitas</span>
                <button className="mapp-sheet-close" onClick={() => setShowFilterModal(false)}>✕</button>
              </div>
              <div className="mapp-sheet-body">
                {renderMobileFilters()}
              </div>
            </div>
          </>
        )}

        {/* Lista */}
        {revenues.length === 0 ? (
          <div className="mapp-empty">
            <div className="mapp-empty-icon">📈</div>
            <p>Nenhuma receita encontrada</p>
          </div>
        ) : revenues.map(revenue => {
          const statusKey = revenue.status === 'Recebida' ? 'recebida'
            : revenue.status === 'Pendente' ? 'pendente'
            : revenue.status === 'Atrasada' ? 'atrasada'
            : 'default';
          return (
            <div key={revenue.id} className={`mapp-item${revenue.is_simulation ? ' simulation' : ''}`}>
              <div className={`mapp-item-accent ${revenue.is_simulation ? 'simulation' : statusKey}`} />
              <div className="mapp-item-body" onClick={() => onEdit(revenue)}>
                <div className="mapp-item-row1">
                  <span className="mapp-item-desc">{revenue.description}</span>
                  <span className="mapp-item-amount revenue">{formatCurrency(revenue.amount)}</span>
                </div>
                <div className="mapp-item-row2">
                  <span className="mapp-item-date">{formatDate(revenue.due_date)}</span>
                  <span className={`mapp-item-badge ${statusKey}`}>{revenue.status}</span>
                  {revenue.is_simulation && <span className="mapp-sim-badge">🧪 Sim.</span>}
                </div>
              </div>
              {revenue.is_simulation && (
                <button
                  className="mapp-item-promote"
                  onClick={(e) => { e.stopPropagation(); handlePromoteToReal(revenue); }}
                  title="Tornar lançamento real"
                >
                  ✓
                </button>
              )}
              <button
                className="mapp-item-del"
                onClick={(e) => { e.stopPropagation(); handleDelete(revenue); }}
                title="Excluir"
              >
                ✕
              </button>
            </div>
          );
        })}

        {renderMobilePagination()}
        <button className="mapp-fab revenue" onClick={onNewRevenue}>+</button>
      </div>
    );
  };

  // ════════════════════════════════════
  // DESKTOP VIEW — redesenhado
  // ════════════════════════════════════
  const renderDesktopView = () => (
    <div>
      <div className="rv-filter-bar">
        <button className="rv-btn rv-btn-outline" onClick={() => setShowFilters(!showFilters)}>
          {showFilters ? '✕ Ocultar Filtros' : '⊞ Filtros'}
        </button>
        <button className="rv-btn rv-btn-simulation" onClick={handleClearSimulations} title="Excluir todas as receitas de simulação">
          🧪 Limpar Simulações
        </button>
      </div>

      {showFilters && renderDesktopFilters()}

      <div className="rv-table-wrapper">
        <table className="rv-table">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Valor</th>
              <th>Vencimento</th>
              <th>Pagamento</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {revenues.map(revenue => (
              <tr key={revenue.id} className={revenue.is_simulation ? 'rv-row-simulation' : ''}>
                <td className="rv-td-desc">
                  {revenue.is_simulation && <span className="sim-badge" title="Simulação">🧪</span>}
                  {revenue.description}
                </td>
                <td className="rv-td-amount">{formatCurrency(revenue.amount)}</td>
                <td>{formatDate(revenue.due_date)}</td>
                <td>{revenue.payment_method}</td>
                <td>
                  <span className={`rv-badge ${getStatusClass(revenue.status)}`}>
                    <span className="rv-badge-dot"></span>
                    {revenue.status}
                  </span>
                </td>
                <td className="rv-td-actions">
                  <div className="rv-td-actions-inner">
                    <button className="rv-btn rv-btn-edit" onClick={() => onEdit(revenue)}>Editar</button>
                    {revenue.is_simulation && (
                      <button className="rv-btn rv-btn-promote" onClick={() => handlePromoteToReal(revenue)} title="Tornar lançamento real">Tornar Real</button>
                    )}
                    <button className="rv-btn rv-btn-delete" onClick={() => handleDelete(revenue)}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {renderDesktopPagination()}
      </div>
    </div>
  );

  return (
    <div>
      {width < 768 ? renderMobileView() : renderDesktopView()}
    </div>
  );
}

export default RevenueList;
